import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SpiralCinematic } from "@/components/SpiralCinematic";
import { CocoonMark } from "@/components/CocoonMark";

export default function Auth() {
  const { t } = useTranslation();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) localStorage.setItem("coccon_ref", ref);
    if (ref || localStorage.getItem("coccon_ref")) {
      setMode("register");
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && !accepted) {
      setError(t("auth.acceptError"));
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else {
        const ref = localStorage.getItem("coccon_ref");
        await register(name, email, password, ref, accepted);
        localStorage.removeItem("coccon_ref");
      }
      const intent = localStorage.getItem("coccon_checkout_intent");
      navigate(intent ? "/tarifs" : "/atelier");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen starfield flex flex-col">
      <div className="p-6 flex items-center justify-between">
        <Link to="/" className="font-display text-2xl tracking-tight" data-testid="auth-brand"><CocoonMark withMind /></Link>
        <LanguageSwitcher />
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="w-full max-w-sm">
          <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-4 text-center">
            {mode === "login" ? t("auth.loginEyebrow") : t("auth.registerEyebrow")}
          </p>
          <h1 className="font-display text-gradient-orange text-center text-4xl sm:text-5xl leading-tight mb-10">
            {mode === "login" ? (
              <>{t("auth.loginTitleA")}<span className="italic text-primary">{t("auth.loginTitleHi")}</span>.</>
            ) : (
              <>{t("auth.registerTitleA")}<span className="italic text-primary">{t("auth.registerTitleHi")}</span>.</>
            )}
          </h1>

          <form onSubmit={submit} className="space-y-7">
            {mode === "register" && (
              <div>
                <label className="block text-xs uppercase tracking-wider text-foreground/40 mb-2">{t("auth.name")}</label>
                <input data-testid="auth-name-input" className="underline-input w-full py-2 text-lg text-foreground" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wider text-foreground/40 mb-2">{t("auth.email")}</label>
              <input data-testid="auth-email-input" type="email" className="underline-input w-full py-2 text-lg text-foreground" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-foreground/40 mb-2">{t("auth.password")}</label>
              <input data-testid="auth-password-input" type="password" className="underline-input w-full py-2 text-lg text-foreground" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive-foreground/90 bg-destructive/20 rounded-lg px-4 py-2">{error}</p>
            )}

            {mode === "register" && (
              <label className="flex items-start gap-3 text-sm text-foreground/60 leading-relaxed cursor-pointer" data-testid="auth-terms-label">
                <input
                  type="checkbox"
                  data-testid="auth-terms-checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 flex-shrink-0 accent-primary rounded"
                />
                <span>
                  {t("auth.acceptPrefix")}{" "}
                  <Link to="/legal/cgu" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="auth-cgu-link">{t("auth.acceptCgu")}</Link>{" "}
                  {t("auth.acceptAnd")}{" "}
                  <Link to="/legal/charte" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="auth-charte-link">{t("auth.acceptCharte")}</Link>.
                </span>
              </label>
            )}

            <button type="submit" data-testid="auth-submit-btn" disabled={loading} className="btn-frost-orange w-full py-4 rounded-full font-semibold disabled:opacity-50">
              {loading ? "…" : mode === "login" ? t("auth.enter") : t("auth.create")}
            </button>
          </form>

          <div className="mt-8 flex items-center gap-4">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase tracking-[0.2em] text-foreground/30">{mode === "login" ? t("auth.newHere") : t("auth.hasAccount")}</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <button data-testid="auth-toggle-btn" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-5 w-full py-4 rounded-full font-semibold border border-primary/45 text-primary hover:bg-primary/10 hover:border-primary/70 transition-colors">
            {mode === "login" ? t("auth.toRegister") : t("auth.toLogin")}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
